import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { scrollToId } from "@/lib/api";

const services = [
  {
    num: "01",
    title: "Ristrutturazioni Complete",
    desc: "Appartamenti, ville e uffici ristrutturati dal grezzo alle finiture. Demolizioni, murature, impianti e arredi coordinati da un unico referente.",
    img: "https://images.unsplash.com/photo-1724582586495-d050726cf354?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwyfHxkYXJrJTIwbW9kZXJuJTIwbHV4dXJ5JTIwbGl2aW5nJTIwcm9vbXxlbnwwfHx8fDE3ODc4ODEwNjB8MA&ixlib=rb-4.1.0&q=85",
  },
  {
    num: "02",
    title: "Bagni & Cucine",
    desc: "Gli ambienti più vissuti della casa rifatti a nuovo: sanitari, rivestimenti, top su misura e impianti a norma, con tempi certi di cantiere.",
    img: "https://images.unsplash.com/photo-1638799869566-b17fa794c4de?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njd8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBtaW5pbWFsaXN0JTIwYmF0aHJvb218ZW58MHx8fHwxNzg3ODgxMDU0fDA&ixlib=rb-4.1.0&q=85",
  },
  {
    num: "03",
    title: "Cappotto & Efficientamento",
    desc: "Cappotto termico, serramenti e isolamenti per tagliare i consumi. Supporto completo su pratiche e detrazioni fiscali.",
    img: "https://images.pexels.com/photos/4458205/pexels-photo-4458205.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  },
  {
    num: "04",
    title: "Manutenzioni",
    desc: "Interventi rapidi e programmati per privati, condomini e aziende: idraulica, cartongessi, tinteggiature e ripristini.",
    img: "https://images.unsplash.com/photo-1638885930125-85350348d266?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBtb2Rlcm4lMjBpbnRlcmlvciUyMHJlbm92YXRpb258ZW58MHx8fHwxNzg3ODgxMDU0fDA&ixlib=rb-4.1.0&q=85",
  },
];

export const Services = () => {
  const [active, setActive] = useState(0);

  return (
    <section id="servizi" className="py-28 lg:py-40" data-testid="services-section">
      <div className="max-w-[1600px] mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
          className="mb-20 lg:mb-28"
        >
          <p className="font-oswald text-gold uppercase tracking-[0.35em] text-sm mb-6">Cosa facciamo</p>
          <h2 className="font-oswald uppercase text-3xl md:text-5xl tracking-[0.08em] leading-tight max-w-3xl" data-testid="services-title">
            Dalle demolizioni alla consegna, <span className="text-gold">tutto nelle nostre mani</span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
          <div className="border-t border-white/10">
            {services.map((s, i) => (
              <motion.button
                key={s.num}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7, delay: i * 0.08, ease: [0.76, 0, 0.24, 1] }}
                onMouseEnter={() => setActive(i)}
                onClick={() => scrollToId("#preventivo")}
                data-testid={`service-row-${s.num}`}
                className={`group w-full text-left py-9 lg:py-11 border-b border-white/10 flex items-start gap-6 lg:gap-10 transition-colors duration-500 px-2 lg:px-4 ${
                  active === i ? "bg-surface" : "bg-transparent"
                }`}
              >
                <span className="flex-1">
                  <span className="flex items-center justify-between gap-4">
                    <span className="font-oswald uppercase text-xl lg:text-2xl tracking-[0.08em] group-hover:text-gold transition-colors duration-300">
                      {s.title}
                    </span>
                    <ArrowUpRight className={`w-5 h-5 shrink-0 transition-all duration-300 ${active === i ? "text-gold translate-x-1 -translate-y-1" : "text-muted-foreground"}`} />
                  </span>
                  <span className="block mt-3 text-sm lg:text-base text-muted-foreground leading-relaxed max-w-md">{s.desc}</span>
                </span>
              </motion.button>
            ))}
          </div>

          <div className="hidden lg:block relative">
            <div className="sticky top-32 aspect-[4/5] bg-surface border border-white/10 p-3">
              <div className="relative w-full h-full overflow-hidden">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={active}
                    src={services[active].img}
                    alt={services[active].title}
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                    className="absolute inset-0 w-full h-full object-cover"
                    data-testid="services-preview-image"
                  />
                </AnimatePresence>
                <div className="absolute bottom-4 left-4 bg-ink/80 backdrop-blur-sm px-4 py-2 border border-white/10">
                  <span className="font-oswald uppercase text-xs tracking-[0.25em] text-gold">{services[active].title}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
